import { EditorView, type KeyBinding } from "@codemirror/view";
import { EditorSelection } from "@codemirror/state";

/**
 * Toggle a heading prefix (e.g. "# ", "## ", "### ") on the current line.
 * - If the line already has the target heading level, remove it.
 * - If the line has a different heading level, replace it.
 * - If the line has no heading, add it.
 */
export function toggleHeading(view: EditorView, level: 1 | 2 | 3): void {
  const state = view.state;
  const { from, to } = state.selection.main;
  const lineStart = state.doc.lineAt(from);
  const lineEnd = state.doc.lineAt(to);

  const prefix = "#".repeat(level) + " ";
  const headingRe = /^(#{1,6})\s/;

  const changes: { from: number; to: number; insert: string }[] = [];
  let cursorOffset = 0;
  let firstLine = true;

  for (let lineNo = lineStart.number; lineNo <= lineEnd.number; lineNo++) {
    const line = state.doc.line(lineNo);
    const match = line.text.match(headingRe);

    if (match) {
      const existingPrefix = match[0]; // e.g. "## "
      if (match[1].length === level) {
        // Same level — remove heading
        changes.push({
          from: line.from,
          to: line.from + existingPrefix.length,
          insert: "",
        });
        if (firstLine) cursorOffset = -existingPrefix.length;
      } else {
        // Different level — replace
        changes.push({
          from: line.from,
          to: line.from + existingPrefix.length,
          insert: prefix,
        });
        if (firstLine) cursorOffset = prefix.length - existingPrefix.length;
      }
    } else {
      // No heading — add
      changes.push({ from: line.from, to: line.from, insert: prefix });
      if (firstLine) cursorOffset = prefix.length;
    }
    firstLine = false;
  }

  const newCursor = Math.max(0, state.selection.main.head + cursorOffset);
  view.dispatch({ changes, selection: EditorSelection.cursor(newCursor) });
}

/**
 * Toggle bold (**) around the selection.
 * No selection: insert **** and place cursor between.
 */
export function toggleBold(view: EditorView): void {
  toggleInlineMarker(view, "**");
}

/**
 * Toggle italic (*) around the selection.
 * No selection: insert ** and place cursor between.
 */
export function toggleItalic(view: EditorView): void {
  toggleInlineMarker(view, "*");
}

/**
 * Toggle inline code (`) around the selection.
 */
export function toggleInlineCode(view: EditorView): void {
  toggleInlineMarker(view, "`");
}

function toggleInlineMarker(view: EditorView, marker: string): void {
  const state = view.state;
  const { from, to } = state.selection.main;
  const len = marker.length;

  if (from === to) {
    // No selection — insert pair and place cursor between
    view.dispatch({
      changes: { from, to, insert: marker + marker },
      selection: EditorSelection.cursor(from + len),
    });
    return;
  }

  const selected = state.sliceDoc(from, to);

  // Check if selection is already wrapped
  if (selected.startsWith(marker) && selected.endsWith(marker) && selected.length >= len * 2) {
    const inner = selected.slice(len, -len);
    view.dispatch({
      changes: { from, to, insert: inner },
      selection: EditorSelection.range(from, from + inner.length),
    });
    return;
  }

  // Check if surrounding text contains the markers
  const before = state.sliceDoc(Math.max(0, from - len), from);
  const after = state.sliceDoc(to, Math.min(state.doc.length, to + len));

  if (before === marker && after === marker) {
    // Remove surrounding markers
    view.dispatch({
      changes: [
        { from: from - len, to: from, insert: "" },
        { from: to, to: to + len, insert: "" },
      ],
      selection: EditorSelection.range(from - len, to - len),
    });
    return;
  }

  // Wrap selection
  view.dispatch({
    changes: { from, to, insert: marker + selected + marker },
    selection: EditorSelection.range(from + len, to + len),
  });
}

/**
 * Toggle `- ` prefix on selected lines (bullet list).
 */
export function toggleBulletList(view: EditorView): void {
  toggleLinePrefix(view, "- ", /^- /);
}

/**
 * Toggle `- [ ] ` prefix on selected lines (checkbox).
 */
export function toggleCheckbox(view: EditorView): void {
  toggleLinePrefix(view, "- [ ] ", /^- \[[ x]\] /);
}

/**
 * Toggle `> ` prefix on selected lines (blockquote).
 */
export function toggleBlockquote(view: EditorView): void {
  toggleLinePrefix(view, "> ", /^> /);
}

function toggleLinePrefix(view: EditorView, prefix: string, prefixRe: RegExp): void {
  const state = view.state;
  const { from, to } = state.selection.main;
  const lineStart = state.doc.lineAt(from);
  const lineEnd = state.doc.lineAt(to);

  // Check if all lines already have the prefix
  let allHavePrefix = true;
  for (let lineNo = lineStart.number; lineNo <= lineEnd.number; lineNo++) {
    if (!prefixRe.test(state.doc.line(lineNo).text)) {
      allHavePrefix = false;
      break;
    }
  }

  const changes: { from: number; to: number; insert: string }[] = [];

  for (let lineNo = lineStart.number; lineNo <= lineEnd.number; lineNo++) {
    const line = state.doc.line(lineNo);
    const match = line.text.match(prefixRe);

    if (allHavePrefix && match) {
      // Remove prefix
      changes.push({
        from: line.from,
        to: line.from + match[0].length,
        insert: "",
      });
    } else if (!allHavePrefix && !match) {
      // Add prefix (also strip other list markers if present)
      const otherMarker = line.text.match(/^(- \[[ x]\] |[-*+] |\d+\. )/);
      if (otherMarker) {
        changes.push({
          from: line.from,
          to: line.from + otherMarker[0].length,
          insert: prefix,
        });
      } else {
        changes.push({ from: line.from, to: line.from, insert: prefix });
      }
    }
  }

  if (changes.length > 0) {
    view.dispatch({ changes });
  }
}

/**
 * Toggle ordered list (`1. `) prefix on selected lines.
 * Auto-renumbers adjacent numbered lines at the same indent level.
 */
export function toggleOrderedList(view: EditorView): void {
  const state = view.state;
  const { from, to } = state.selection.main;
  const lineStart = state.doc.lineAt(from);
  const lineEnd = state.doc.lineAt(to);

  const orderedRe = /^(\d+)\. /;

  // Check if all selected lines already have ordered list prefix
  let allHavePrefix = true;
  for (let lineNo = lineStart.number; lineNo <= lineEnd.number; lineNo++) {
    if (!orderedRe.test(state.doc.line(lineNo).text)) {
      allHavePrefix = false;
      break;
    }
  }

  const changes: { from: number; to: number; insert: string }[] = [];

  if (allHavePrefix) {
    // Remove all ordered list prefixes
    for (let lineNo = lineStart.number; lineNo <= lineEnd.number; lineNo++) {
      const line = state.doc.line(lineNo);
      const match = line.text.match(orderedRe);
      if (match) {
        changes.push({
          from: line.from,
          to: line.from + match[0].length,
          insert: "",
        });
      }
    }
  } else {
    // Add/replace prefixes with sequential numbers
    let num = 1;
    for (let lineNo = lineStart.number; lineNo <= lineEnd.number; lineNo++) {
      const line = state.doc.line(lineNo);
      const match = line.text.match(orderedRe);
      const otherMarker = line.text.match(/^(- \[[ x]\] |[-*+] )/);
      const prefix = `${num}. `;

      if (match) {
        // Replace existing number
        changes.push({
          from: line.from,
          to: line.from + match[0].length,
          insert: prefix,
        });
      } else if (otherMarker) {
        // Replace other list marker
        changes.push({
          from: line.from,
          to: line.from + otherMarker[0].length,
          insert: prefix,
        });
      } else {
        changes.push({ from: line.from, to: line.from, insert: prefix });
      }
      num++;
    }
  }

  if (changes.length > 0) {
    view.dispatch({ changes });
    // Renumber adjacent ordered list lines after the selection
    renumberAdjacentLines(view, lineEnd.number);
  }
}

/**
 * Walk lines after `startLineNo` and renumber any contiguous ordered list lines.
 */
function renumberAdjacentLines(view: EditorView, startLineNo: number): void {
  const state = view.state;
  const orderedRe = /^(\d+)\. /;
  const changes: { from: number; to: number; insert: string }[] = [];

  // Find what number the last ordered line in the current block has
  const startLine = state.doc.line(startLineNo);
  const startMatch = startLine.text.match(orderedRe);
  if (!startMatch) return;

  let nextNum = parseInt(startMatch[1], 10) + 1;

  for (let lineNo = startLineNo + 1; lineNo <= state.doc.lines; lineNo++) {
    const line = state.doc.line(lineNo);
    const match = line.text.match(orderedRe);
    if (!match) break;

    const currentNum = parseInt(match[1], 10);
    if (currentNum !== nextNum) {
      changes.push({
        from: line.from,
        to: line.from + match[0].length,
        insert: `${nextNum}. `,
      });
    }
    nextNum++;
  }

  if (changes.length > 0) {
    view.dispatch({ changes });
  }
}

/**
 * Toggle a fenced code block (```) around the selection.
 */
export function toggleCodeBlock(view: EditorView): void {
  const state = view.state;
  const { from, to } = state.selection.main;
  const lineStart = state.doc.lineAt(from);
  const lineEnd = state.doc.lineAt(to);

  const fence = "```";

  // Check if selection is already inside a code block
  // Look for ``` on the line before the first selected line and after the last
  const prevLineNo = lineStart.number - 1;
  const nextLineNo = lineEnd.number + 1;

  const prevLine = prevLineNo >= 1 ? state.doc.line(prevLineNo) : null;
  const nextLine = nextLineNo <= state.doc.lines ? state.doc.line(nextLineNo) : null;

  if (
    prevLine &&
    nextLine &&
    prevLine.text.startsWith(fence) &&
    nextLine.text.trimEnd() === fence
  ) {
    // Remove the fences
    view.dispatch({
      changes: [
        { from: prevLine.from, to: lineStart.from, insert: "" },
        { from: lineEnd.to, to: nextLine.to, insert: "" },
      ],
    });
    return;
  }

  // Check if selection itself starts/ends with fences
  if (
    lineStart.text.startsWith(fence) &&
    lineEnd.text.trimEnd() === fence &&
    lineStart.number !== lineEnd.number
  ) {
    // Remove the fences (they are part of the selection)
    view.dispatch({
      changes: [
        { from: lineStart.from, to: state.doc.line(lineStart.number + 1).from, insert: "" },
        {
          from: state.doc.line(lineEnd.number - 1).to,
          to: lineEnd.to,
          insert: "",
        },
      ],
    });
    return;
  }

  // Wrap in code block
  const selected = state.sliceDoc(from, to);
  const beforeNewline = from === lineStart.from ? "" : "\n";
  const afterNewline = to === lineEnd.to ? "" : "\n";

  const insert = `${beforeNewline}${fence}\n${selected}\n${fence}${afterNewline}`;
  const contentStart = from + beforeNewline.length + fence.length + 1;

  view.dispatch({
    changes: { from, to, insert },
    selection: EditorSelection.range(contentStart, contentStart + selected.length),
  });
}

/**
 * Insert a horizontal rule (---) on its own line.
 */
export function insertHorizontalRule(view: EditorView): void {
  const state = view.state;
  const pos = state.selection.main.head;
  const line = state.doc.lineAt(pos);

  // If the current line is empty, insert directly; otherwise add a newline first
  const prefix = line.text.trim() === "" ? "" : "\n";
  const insert = `${prefix}---\n`;

  view.dispatch({
    changes: { from: line.to, to: line.to, insert },
    selection: EditorSelection.cursor(line.to + insert.length),
  });
}

/**
 * Insert a markdown link.
 * With selection: wraps as [selection](url) and places cursor in URL part.
 * Without selection: inserts [](url) with cursor inside brackets.
 */
export function insertLink(view: EditorView): void {
  const state = view.state;
  const { from, to } = state.selection.main;

  if (from === to) {
    // No selection — insert [](url) with cursor inside []
    const insert = "[](url)";
    view.dispatch({
      changes: { from, to, insert },
      selection: EditorSelection.cursor(from + 1),
    });
  } else {
    // Has selection — wrap as [text](url) with cursor selecting "url"
    const text = state.sliceDoc(from, to);
    const insert = `[${text}](url)`;
    const urlStart = from + 1 + text.length + 2; // [text](
    view.dispatch({
      changes: { from, to, insert },
      selection: EditorSelection.range(urlStart, urlStart + 3),
    });
  }
}

/**
 * Keyboard shortcuts for markdown commands.
 */
export const markdownKeymap: KeyBinding[] = [
  {
    key: "Mod-b",
    run: (view) => {
      toggleBold(view);
      return true;
    },
  },
  {
    key: "Mod-i",
    run: (view) => {
      toggleItalic(view);
      return true;
    },
  },
  {
    key: "Mod-k",
    run: (view) => {
      insertLink(view);
      return true;
    },
  },
  {
    key: "Mod-e",
    run: (view) => {
      toggleInlineCode(view);
      return true;
    },
  },
];
